import React, {
    Children,
    cloneElement,
    FC,
    FunctionComponentElement,
    PropsWithRef,
    RefAttributes,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { isMobile } from '@direct-frontend/platform-code';
import { mergeRefs } from '@direct-frontend/stdlib';

import { Tooltip, TooltipProps } from '../Tooltip';
import { forkFn } from '../../lib/function/forkFn/forkFn';

import { HandlersProps, Trigger } from './types';

export interface TriggerProps extends RefAttributes<HTMLElement> {
    onClick?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onHoverStart?: () => void;
    onHoverEnd?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

export interface StatefulTooltipProps extends Omit<TooltipProps, 'isOpen' | 'triggerRef' | 'onClose'>, Loggable {
    /**
     * Элемент, при взаимодействии с которым происходит отображение тултипа
     */
    children: FunctionComponentElement<PropsWithRef<TriggerProps>>;

    /**
     * Задержка, после которой закрывается тултип
     * @default 300
     */
    delay?: number;

    /**
     * Задержка, после которой открывается тултип
     * @default undefined
     */
    openDelay?: number;

    /**
     * Способ или способы, используемые для открытия/закрытия тултипа
     * @default 'hover'
     */
    trigger?: Trigger | Trigger[];
}

export const StatefulTooltip: FC<StatefulTooltipProps> = props => {
    const { LogProvider } = useLogger('StatefulTooltip', props.logNode);

    const [isOpen, setIsOpen] = useState(false);
    const { children, delay = 300, trigger = 'hover', openDelay, isDismissable, logNode, ...tooltipProps } = props;

    const closingTimer = useRef<ReturnType<typeof setTimeout>>();
    const isTriggerHovered = useRef(false);
    const openTimer = useRef<ReturnType<typeof setTimeout>>();

    const clearTimers = () => {
        if (closingTimer.current) {
            clearTimeout(closingTimer.current);
        }

        if (openTimer.current) {
            clearTimeout(openTimer.current);
        }
    };
    useEffect(() => clearTimers, []);

    const close = useCallback(() => {
        if (!isTriggerHovered.current) {
            setIsOpen(false);
        }
    }, []);

    const closeTooltip = useCallback(() => {
        if (isMobile()) {
            close();

            return;
        }
        clearTimers();
        closingTimer.current = setTimeout(close, delay);
    }, [delay, close]);

    const openTooltip = useCallback(() => {
        clearTimers();
        setIsOpen(true);
    }, []);

    const toggleTooltip = useCallback(() => {
        clearTimers();
        setIsOpen(!isOpen);
    }, [isOpen]);

    const child = Children.only(children);

    const triggers = useMemo(() => (Array.isArray(trigger) ? trigger : [trigger]), [trigger]);
    const openWithDelay = useCallback(() => {
        if (openDelay !== undefined) {
            clearTimers();
            openTimer.current = setTimeout(() => {
                openTooltip();
            }, openDelay);
        } else {
            openTooltip();
        }
    }, [openDelay, openTooltip]);
    const handlers: HandlersProps = useMemo(
        () =>
            // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
            triggers.reduce<HandlersProps>((acc, trigger) => {
                switch (trigger) {
                    case 'click':
                        acc.onClick = forkFn(child.props.onClick, () => toggleTooltip());
                        break;
                    case 'hover':
                        if (isMobile()) {
                            acc.onClick = forkFn(child.props.onClick, () => toggleTooltip());

                            break;
                        }

                        if (typeof child.type !== 'string') {
                            acc.onHoverStart = forkFn(child.props.onHoverStart, () => {
                                isTriggerHovered.current = true;
                                openWithDelay();
                            });
                            acc.onHoverEnd = forkFn(child.props.onHoverEnd, () => {
                                isTriggerHovered.current = false;
                                closeTooltip();
                            });
                        }
                        acc.onMouseEnter = forkFn(child.props.onMouseEnter, () => {
                            isTriggerHovered.current = true;
                            openWithDelay();
                        });
                        acc.onMouseLeave = forkFn(child.props.onMouseLeave, () => {
                            isTriggerHovered.current = false;
                            closeTooltip();
                        });

                        break;
                }
                return acc;
            }, {}),
        [child, closeTooltip, openWithDelay, toggleTooltip, triggers],
    );

    const triggerRef = useRef<HTMLElement>(null);

    const hasHoverTrigger = useMemo(() => triggers.includes('hover'), [triggers]);
    const onMouseEnter = hasHoverTrigger ? openTooltip : undefined;
    const onMouseLeave = hasHoverTrigger ? closeTooltip : undefined;

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    const ref = useMemo(() => (child.ref === undefined ? triggerRef : mergeRefs(child.ref, triggerRef)), [child.ref]);
    const defaultIsDismissable = useMemo(() => triggers.length === 1 && triggers[0] === 'click', [triggers]);
    const isAddedToLayerManager = !triggers.some(trigger => trigger === 'hover');
    const hasContent = Boolean(props.title) || Boolean(props.customContent);

    return (
        <LogProvider>
            {hasContent && (
                <Tooltip
                    key="tooltip"
                    {...tooltipProps}
                    isDismissable={isDismissable ?? defaultIsDismissable}
                    isAddedToLayerManager={isAddedToLayerManager}
                    isOpen={isOpen}
                    onClose={closeTooltip}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    triggerRef={triggerRef}
                    data-testid={props['data-testid']}
                />
            )}
            {cloneElement(child, {
                key: 'trigger',
                ...handlers,
                ref,
            })}
        </LogProvider>
    );
};
